import axios from 'axios'
import { getApiBasePath } from './config/apiBase'

export const api = axios.create({
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  config.baseURL = getApiBasePath()
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => {
    if (r.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return r
  },
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    const msg = err.response?.data?.detail || err.message || 'Network error'
    return Promise.reject(new Error(typeof msg === 'string' ? msg : JSON.stringify(msg)))
  }
)

export async function getDrones() {
  const { data } = await api.get('/drones')
  return data
}

export async function getDrone(id: string) {
  const { data } = await api.get(`/drones/${id}`)
  return data
}

export async function patchDrone(
  id: string,
  body: {
    camera_stream_url?: string
    notes?: string
    phone_number?: string
    status?: string
    battery_level?: number
    last_lat?: string
    last_lng?: string
    altitude?: number
    speed?: number
    roll?: number
    pitch?: number
    heading?: number
    gps_fix_type?: number
    flight_mode?: string
  }
) {
  const { data } = await api.patch(`/drones/${id}`, body)
  return data
}

export async function commTestDrone(id: string): Promise<{
  status: 'healthy' | 'degraded' | 'unreachable'
  drone_id: string
  phone_number: string | null
  ping: boolean
  latency_ms: number | null
  signal_strength: number
  sim_configured: boolean
  sim_registered: boolean
  sms_capable: boolean
  sms_sent: boolean
  sms_ack: boolean
  last_heartbeat: string | null
  flight_mode: string | null
  checked_at: number
}> {
  const { data } = await api.post(`/drones/${id}/comm-test`, {})
  return data
}

export async function getEvents() {
  const { data } = await api.get('/events')
  return data
}

export async function getDeliveries() {
  const { data } = await api.get('/deliveries')
  return data
}

export async function getMissions() {
  const { data } = await api.get('/missions')
  return data
}

export async function getLicenses() {
  const { data } = await api.get('/licenses')
  return data
}

export async function createMission(body: { drone_id: string; name: string; type: string; waypoints: Array<{ latitude: number; longitude: number; altitude?: number }> }) {
  const { data } = await api.post('/missions', body)
  return data
}

export async function createDelivery(body: {
  delivery_number: string
  recipient_name?: string
  recipient_phone?: string
  pickup_location: { latitude: number; longitude: number; address?: string }
  delivery_location: { latitude: number; longitude: number; address?: string }
  /** m/s — legacy default 8; used for ETA on server */
  speed_mps?: number
}) {
  const { data } = await api.post('/deliveries', body)
  return data
}

export async function patchDelivery(
  id: string,
  body: { status?: string; drone_id?: string; mission_id?: string }
) {
  const { data } = await api.patch(`/deliveries/${id}`, body)
  return data
}

// ─── Licenses (unified drone license / serial ID) ──────────────────────────

export async function createLicense(body: { drone_uuid: string; tail_number?: string }) {
  const { data } = await api.post('/licenses', body)
  return data
}

export async function deleteLicense(id: string) {
  const { data } = await api.delete(`/licenses/${id}`)
  return data
}

// ─── Customer Setup helpers ────────────────────────────────────────────────

export async function getCustomerSetups() {
  const { data } = await api.get('/customer-setup')
  return data
}

// ─── Alerts ────────────────────────────────────────────────────────────────

export const getAlerts = (params?: { source?: string; unread?: boolean }) => {
  const q = new URLSearchParams(params as any).toString()
  return api.get(`/api/v1/alerts?limit=30${q ? '&' + q : ''}`).then(r => r.data)
}
export const createAlert = (body: { drone_id?: string; source: string; severity: string; title: string; message?: string }) =>
  api.post('/api/v1/alerts', body).then(r => r.data)

// ─── Operations Logs ───────────────────────────────────────────────────────

export const getLogs = (params?: { drone_id?: string; user_id?: string; action_type?: string }) => {
  const q = new URLSearchParams(params as any).toString()
  return api.get(`/api/v1/logs${q ? '?' + q : ''}`).then(r => r.data)
}
export const createLog = (body: { drone_id?: string; mission_id?: string; action_type: string; control_mode?: string; result?: string; notes?: string }) =>
  api.post('/api/v1/logs', body).then(r => r.data)
